import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, FormHelperText, Typography,
    useMediaQuery, useTheme,
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import Spinner from "../../spinner/Spinner";
import {connect} from "react-redux";
import {alertActions} from "../../../store/actions/alertActions";
import ReactHookFormRadio from "../../input/ReactHookFormRadio";
import BotConditions from "./BotConditions";
import ReactHookFormTextField from "../../input/ReactHookFormTextField";
import {useParams} from "react-router-dom";
import {botRulesServices} from "../../../service/botRulesService";
import {botRulesActions} from "../../../store/actions/botRulesActions";

import {getSettingsColors, settingsInputStyles} from "../SettingsDesign";

function BotRulesForm(props) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const {id} = useParams();

    const {errorMsg, successMsg, rerenderBotRules} = props;

    const {reset, register, handleSubmit, control, setValue, getValues, formState: {errors}} = useForm({
        defaultValues: {
            name: "",
            logicalRelation: "and",
            rules: {}
        }
    });

    const [loading, setLoading] = useState(false);

    const radioConditions = [
        {label: "And", value: "and", color: "primary"},
        {label: "Or", value: "or", color: "primary"},
    ];

    const handleOnClose = () => {
        props.history.push({pathname: "/settings/bot-rules"});
    }

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        handleOnClose();
    };

    // Fetch data by ID if present in the URL
    useEffect(() => {
        const fetchBotRuleData = async () => {
            setLoading(true);
            try {
                await botRulesServices.find({id}).then((result) => {
                    reset({
                        name: result?.name || "",
                        logicalRelation: result?.logicalRelation || "and",
                        rules: result?.rules || {}
                    });
                });
            } catch (error) {
                console.error("Failed fetching bot rule records:", error?.response?.data?.error);
                errorMsg("Failed to load bot rules. " + error?.response?.data?.error);
            } finally {
                setLoading(false);
            }
        };

        if (id > 0 && id !== null && id !== undefined) {
            fetchBotRuleData();
        }
    }, [errorMsg, id, reset]);

    const onSubmit = async (formData) => {
        setLoading(true);
        try {
            if (id) {
                // Update bot rule
                await botRulesServices.update({id, formData}).then(() => {
                    successMsg("Bot rule has been successfully updated.");
                    rerenderBotRules(true);
                    handleClose();
                });
            } else {
                // create a new bot rule
                await botRulesServices.create({formData}).then(() => {
                    successMsg("Bot rule has been created successfully.");
                    rerenderBotRules(true);
                    handleClose();
                });
            }
        } catch (error) {
            console.error("Error submitting bot rule form:", error?.response?.data?.error);
            errorMsg("Failed to save bot rule. " + error?.response?.data?.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={true}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                aria-labelledby="create-bot-rule-form"
                maxWidth={"md"}
                slotProps={{paper: {sx: {
                    bgcolor: getSettingsColors(theme).card, color: getSettingsColors(theme).text,
                    border: theme.palette.mode === 'dark' ? 1 : 0,
                    borderColor: getSettingsColors(theme).border, borderRadius: fullScreen ? 0 : '16px',
                    ...settingsInputStyles
                }}}}
        >
            <DialogTitle id="create-bot-rule-form" sx={{m: 0, px: 3, py: 2.5,
                bgcolor: getSettingsColors(theme).card}}>
                <Typography component="span" sx={{display: 'block', fontSize: 22, fontWeight: 700}}>{id ? 'Edit bot rule' : 'Create bot rule'}</Typography>
                <Typography component="span" sx={{display: 'block', mt: 0.5, fontSize: 13, fontWeight: 400,
                    color: getSettingsColors(theme).muted, whiteSpace: 'normal'}}>Choose the conditions that identify bot traffic in your campaigns.</Typography>
            </DialogTitle>
            <DialogContent sx={{p: {xs: 2, sm: 3}, scrollbarGutter: 'stable'}}>
                <Box display="flex" flexDirection="column">
                    <Box sx={{mt: 2, p: 0, bgcolor: 'transparent'}}>
                        <ReactHookFormTextField
                            register={register}
                            errors={errors}
                            name="name"
                            label="Rule name"
                            type="text"
                            placeholder="Enter a rule name"
                            control={control}
                            rules={{
                                required: true,
                                pattern: {
                                    value: /^[a-zA-Z0-9.\-{}()[\]\s]+$/,
                                    message: "The following characters are permitted a-zA-Z0-9.-{}[]()"
                                }
                            }}
                            slotProps={{
                                inputLabel: {
                                    shrink: true
                                },
                                sx: {fontSize: "14px"},
                            }}
                        />
                        <FormHelperText sx={{color: "error.main"}}>
                            {errors?.name?.message}
                        </FormHelperText>
                    </Box>
                    <Box mt={3} mb={1} sx={{fontSize: "14px", fontWeight: 600}}>
                        Logical relation between conditions
                    </Box>
                    <Box sx={{px: 2, py: 0.5, bgcolor: getSettingsColors(theme).inset, borderRadius: '8px'}}>
                        <ReactHookFormRadio
                            defaultValue="and"
                            control={control}
                            options={radioConditions}
                            name="logicalRelation"
                            label="Logical Relation"
                        />
                    </Box>
                    <Box pt={3}>
                        <Typography sx={{fontSize: 14, fontWeight: 600, mb: 1}}>Detection conditions</Typography>
                        <BotConditions
                            control={control}
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            getValues={getValues}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{m: 0, px: 3, py: 2, bgcolor: getSettingsColors(theme).card,
                '& .MuiButton-root': {borderRadius: '8px'}}}>
                <Button onClick={handleClose} color="inherit" variant="text">
                    Cancel
                </Button>
                {loading ? <Spinner/> :
                    <Button onClick={handleSubmit(onSubmit)} color="primary" variant="contained">
                        Save & Close
                    </Button>
                }
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
        return {
            state: {
                alert: state.alert,
            }
        };
    }
;

const mapDispatchToProps = {
    errorMsg: alertActions.error,
    successMsg: alertActions.success,
    clear: alertActions.clear,
    rerenderBotRules: botRulesActions.rerender
};

export default connect(mapStateToProps, mapDispatchToProps)(BotRulesForm);
